import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import {
  drive_create_share_path,
  drive_create_folder_share_path,
  share_targets_drive_index_path
} from '../../routes'
import { useBootstrapModal, csrfToken } from '../../modules/drive/bootstrap-modal.js'
import { useDriveI18n, useDriveUi } from './drive-context.js'

async function loadShareTargets() {
  const res = await fetch(share_targets_drive_index_path({ format: 'json' }), {
    headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return res.json()
}

export function ShareModal({ open, target, onClose }) {
  const i18n = useDriveI18n()
  const ui = useDriveUi()
  const t = (key) => i18n.t(key)
  const publicAllowed = !!ui.publicShareAllowed

  const [shareType, setShareType] = useState(publicAllowed ? 'public' : 'internal')
  const [duration, setDuration] = useState('7')
  const [people, setPeople] = useState([])
  const [groups, setGroups] = useState([])
  const [peopleQuery, setPeopleQuery] = useState('')
  const [groupsQuery, setGroupsQuery] = useState('')
  const [selectedPeople, setSelectedPeople] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [phase, setPhase] = useState('form') // form | result
  const [shareUrl, setShareUrl] = useState('')
  const [resultSummary, setResultSummary] = useState(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleHidden = useCallback(() => onClose?.(), [onClose])
  const modalRef = useBootstrapModal(open, handleHidden)

  useEffect(() => {
    if (!open) return
    setShareType(publicAllowed ? 'public' : 'internal')
    setDuration('7')
    setSelectedPeople([])
    setSelectedGroups([])
    setPeopleQuery('')
    setGroupsQuery('')
    setPhase('form')
    setShareUrl('')
    setResultSummary(null)
    setCopied(false)
    loadShareTargets()
      .then((data) => {
        setPeople(data.people || [])
        setGroups(data.groups || [])
      })
      .catch((e) => console.error(e))
  }, [open, publicAllowed, target])

  const filteredPeople = people.filter((p) => {
    const q = peopleQuery.trim().toLowerCase()
    if (!q) return true
    return (p.name || '').toLowerCase().includes(q)
  })
  const filteredGroups = groups.filter((g) => {
    const q = groupsQuery.trim().toLowerCase()
    if (!q) return true
    return (g.name || '').toLowerCase().includes(q)
  })

  async function createShare() {
    if (!target?.id) return
    setBusy(true)
    try {
      const url = target.type === 'folder'
        ? drive_create_folder_share_path(target.id)
        : drive_create_share_path(target.id)
      const body = shareType === 'public'
        ? { is_public: true, duration: Number(duration) }
        : {
            is_public: false,
            person_ids: selectedPeople,
            person_group_ids: selectedGroups
          }
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken()
        },
        body: JSON.stringify(body)
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.errors?.[0] || data.error || `HTTP ${res.status}`)
      setPhase('result')
      if (shareType === 'public') {
        setShareUrl(data.url || data.share_url || '')
      } else {
        setResultSummary(data)
      }
    } catch (e) {
      console.error(e)
      alert(e.message || t('drive.error'))
    } finally {
      setBusy(false)
    }
  }

  function copyLink() {
    if (!shareUrl) return
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    })
  }

  const title = target?.type === 'folder' ? t('drive.share_folder') : t('drive.share_file')

  return (
    <div class="modal fade" tabindex="-1" aria-hidden="true" ref={modalRef}>
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bi bi-share me-2" />{title}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label={t('helpers.close')} />
          </div>
          <div class="modal-body">
            {phase === 'form' && (
              <>
                <div class="mb-4">
                  <label class="form-label fw-bold">{t('drive.share_type')}</label>
                  <div class="btn-group w-100" role="group">
                    <input type="radio" class="btn-check" name="share-type-preact" id="share-type-public-p" checked={shareType === 'public'} disabled={!publicAllowed} onChange={() => setShareType('public')} />
                    <label class={`btn btn-outline-primary${!publicAllowed ? ' disabled' : ''}`} for="share-type-public-p" title={!publicAllowed ? ui.publicShareLockTitle : undefined}>
                      <i class="bi bi-globe me-2" />{t('drive.public_share')}
                      {!publicAllowed && <i class="bi bi-lock-fill text-warning ms-1" />}
                    </label>
                    <input type="radio" class="btn-check" name="share-type-preact" id="share-type-internal-p" checked={shareType === 'internal'} onChange={() => setShareType('internal')} />
                    <label class="btn btn-outline-primary" for="share-type-internal-p">
                      <i class="bi bi-people me-2" />{t('drive.internal_share')}
                    </label>
                  </div>
                  {!publicAllowed && ui.publicShareLockTitle && (
                    <div class="mt-2 small text-muted">{ui.publicShareLockTitle}</div>
                  )}
                </div>

                {shareType === 'public' ? (
                  <div>
                    <div class="mb-3">
                      <label class="form-label">{t('drive.share_duration')}</label>
                      <select class="form-select" value={duration} onChange={(e) => setDuration(e.target.value)}>
                        <option value="1">{t('drive.share_1_day')}</option>
                        <option value="7">{t('drive.share_7_days')}</option>
                        <option value="30">{t('drive.share_30_days')}</option>
                        <option value="90">{t('drive.share_90_days')}</option>
                        <option value="365">{t('drive.share_1_year')}</option>
                        <option value="0">{t('drive.share_permanent')}</option>
                      </select>
                    </div>
                    <div class="text-muted small"><i class="bi bi-info-circle me-1" />{t('drive.public_share_info')}</div>
                  </div>
                ) : (
                  <div>
                    <div class="mb-3">
                      <label class="form-label">{t('drive.share_with_people')}</label>
                      <input
                        type="search"
                        class="form-control mb-2"
                        placeholder={t('drive.share_people_search')}
                        value={peopleQuery}
                        onInput={(e) => setPeopleQuery(e.target.value)}
                      />
                      <select class="form-select" multiple size="8" value={selectedPeople} onChange={(e) => setSelectedPeople([...e.target.selectedOptions].map((o) => o.value))}>
                        {filteredPeople.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <div class="form-text">{t('drive.select_multiple_hint')}</div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label">{t('drive.share_with_groups')}</label>
                      <input
                        type="search"
                        class="form-control mb-2"
                        placeholder={t('drive.share_groups_search')}
                        value={groupsQuery}
                        onInput={(e) => setGroupsQuery(e.target.value)}
                      />
                      <select class="form-select" multiple size="5" value={selectedGroups} onChange={(e) => setSelectedGroups([...e.target.selectedOptions].map((o) => o.value))}>
                        {filteredGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                      <div class="form-text">{t('drive.select_multiple_hint')}</div>
                    </div>
                    <div class="text-muted small"><i class="bi bi-info-circle me-1" />{t('drive.internal_share_info')}</div>
                  </div>
                )}
              </>
            )}

            {phase === 'result' && (
              <div>
                <div class="alert alert-success"><i class="bi bi-check-circle me-2" />{t('drive.share_created')}</div>
                {shareUrl && (
                  <div class="mb-3">
                    <label class="form-label">{t('drive.share_link')}</label>
                    <div class="input-group">
                      <input type="text" class="form-control" readonly value={shareUrl} />
                      <button class="btn btn-outline-secondary" type="button" onClick={copyLink}><i class="bi bi-clipboard" /></button>
                    </div>
                    {copied && <div class="form-text text-success"><i class="bi bi-check2 me-1" />{t('drive.link_copied')}</div>}
                  </div>
                )}
                {resultSummary && !shareUrl && (
                  <p class="mb-0 text-muted">{t('drive.shared_with')}</p>
                )}
              </div>
            )}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{t('helpers.close')}</button>
            {phase === 'form' && (
              <button type="button" class="btn btn-primary" disabled={busy} onClick={createShare}>
                {t('drive.create_share')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
